import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { Card, CardTitle, Grid, MetricCard, Pill, Btn, StatTile, EmptyState } from '../components/UI';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const C = {
  teal: '#314F5C', mid: '#6F8FA7', light: '#AFE1F6', sage: '#819D84', brown: '#9F7E4F',
  text: '#0f1e28', textMid: '#4a6878', textMuted: '#8aacbc',
  border: '#e2ebf0', bg: '#f2f1ef', white: '#fff',
  green: '#17a86a', greenBg: '#eaf7f2', greenText: '#0d6640',
  red: '#e53935', redBg: '#fef0ef', redText: '#8b1c1c',
  amber: '#d97706', amberBg: '#fef3e2', amberText: '#7c4a0a',
  blue: '#2563eb', blueBg: '#eff4ff', blueText: '#1e3a8a',
};

function fmt(n) { return n == null ? '…' : n.toLocaleString(); }
function fmtCurrency(amount, currency) {
  if (amount == null) return '—';
  const sym = { USD: '$', EUR: '€', GBP: '£' }[currency] || currency + ' ';
  return sym + Number(amount).toFixed(2);
}
function timeLabel(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}
function timeAgo(ts) {
  if (!ts) return 'Never';
  const ms = Date.now() - new Date(ts);
  if (ms < 60000)   return 'just now';
  if (ms < 3600000)  return Math.round(ms / 60000) + 'm ago';
  if (ms < 86400000) return Math.round(ms / 3600000) + 'h ago';
  return Math.round(ms / 86400000) + 'd ago';
}

function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setMob(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
}

function Skel({ h = 36, mb = 4 }) {
  return (
    <div style={{ height: h, borderRadius: 6, marginBottom: mb,
      background: 'linear-gradient(90deg,#edf2f5 25%,#f5f7f9 50%,#edf2f5 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.6s ease infinite' }} />
  );
}

/* Status → pill type mapping */
const statusPill = (s) => {
  if (!s) return { type: 'teal', label: 'unknown' };
  const m = {
    active: 'green', approved: 'green', verified: 'green', completed: 'green',
    pending: 'amber', pending_review: 'amber', in_review: 'amber',
    suspended: 'red', cancelled: 'red', rejected: 'red',
    confirmed: 'blue', draft: 'teal',
  };
  return { type: m[s] || 'teal', label: s.replace(/_/g, ' ') };
};

/* ─── Input components (inline, matching UI.js style) ───────────────────── */
function Input({ value, onChange, placeholder, style }) {
  return (
    <input value={value} onChange={onChange} placeholder={placeholder} style={{
      fontSize: '.85rem', border: `1px solid ${C.border}`, borderRadius: 8,
      padding: '8px 11px', color: C.text, outline: 'none',
      fontFamily: F, width: '100%', background: C.white, ...style,
    }} />
  );
}
function Select({ value, onChange, children, style }) {
  return (
    <select value={value} onChange={onChange} style={{
      fontSize: '.82rem', border: `1px solid ${C.border}`, borderRadius: 8,
      padding: '7px 11px', color: C.text, outline: 'none',
      fontFamily: F, background: C.white, width: '100%', cursor: 'pointer', ...style,
    }}>
      {children}
    </select>
  );
}
function Label({ children }) {
  return <div style={{ fontSize: '.73rem', color: C.textMid, fontWeight: 600, marginBottom: 4, letterSpacing: '.01em' }}>{children}</div>;
}
function FieldRow({ children }) {
  return <div style={{ marginBottom: 12 }}>{children}</div>;
}

/* ─── Overlay / Slide-in panel ──────────────────────────────────────────── */
function SlidePanel({ open, onClose, width = 420, isMobile, title, children }) {
  if (!open) return null;
  const panelW = isMobile ? '100vw' : width;
  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(12,28,40,0.25)', zIndex: 900,
        transition: 'opacity .2s', opacity: open ? 1 : 0,
      }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: panelW, maxWidth: '100vw',
        background: C.white, zIndex: 910, overflowY: 'auto', padding: '24px 22px',
        boxShadow: '-4px 0 24px rgba(12,28,40,0.12)', fontFamily: F,
        transform: open ? 'translateX(0)' : 'translateX(100%)', transition: 'transform .25s ease',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: '.95rem', fontWeight: 700, color: C.text }}>{title}</div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: C.textMid, padding: '4px 8px',
          }}>&times;</button>
        </div>
        {children}
      </div>
    </>
  );
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   PARTNERS TAB
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
function PartnersTab({ isMobile }) {
  const [partners, setPartners] = useState([]);
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addingListing, setAddingListing] = useState(false);
  const [newListing, setNewListing] = useState({ title: '', category: '', price_amount: '', price_currency: 'USD', price_unit: 'per session', description: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: l }, { data: b }, { data: r }] = await Promise.all([
        supabase.from('partners').select('*').order('created_at', { ascending: false }),
        supabase.from('partner_listings').select('*').order('sort_order', { ascending: true }),
        supabase.from('bookings').select('id,partner_id,status,quoted_price,quoted_currency,final_price').limit(2000),
        supabase.from('booking_reviews').select('id,partner_id,rating').limit(2000),
      ]);
      setPartners(p || []);
      setListings(l || []);
      setBookings(b || []);
      setReviews(r || []);
    } catch (_e) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  /* Derived counts */
  const listingsByPartner = {};
  listings.forEach(l => { listingsByPartner[l.partner_id] = (listingsByPartner[l.partner_id] || 0) + 1; });
  const bookingsByPartner = {};
  bookings.forEach(b => { bookingsByPartner[b.partner_id] = (bookingsByPartner[b.partner_id] || []).concat(b); });
  const reviewsByPartner = {};
  reviews.forEach(r => { reviewsByPartner[r.partner_id] = (reviewsByPartner[r.partner_id] || []).concat(r); });

  const verified = partners.filter(p => p.verified).length;
  const pending = partners.filter(p => p.status === 'pending' || p.status === 'pending_review').length;
  const activeListings = listings.filter(l => l.active).length;

  /* Filters */
  const types = [...new Set(partners.map(p => p.partner_type).filter(Boolean))].sort();
  const filtered = partners.filter(p => {
    if (search) {
      const q = search.toLowerCase();
      if (!(p.name || '').toLowerCase().includes(q) &&
          !(p.city || '').toLowerCase().includes(q) &&
          !(p.country || '').toLowerCase().includes(q)) return false;
    }
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (typeFilter !== 'all' && p.partner_type !== typeFilter) return false;
    return true;
  });

  /* Partner detail data */
  const sel = selected ? partners.find(p => p.id === selected) : null;
  const selListings = selected ? listings.filter(l => l.partner_id === selected) : [];
  const selBookings = selected ? (bookingsByPartner[selected] || []) : [];
  const selReviews = selected ? (reviewsByPartner[selected] || []) : [];
  const selRevenue = selBookings.reduce((s, b) => s + (parseFloat(b.final_price || b.quoted_price) || 0), 0);
  const selAvgRating = selReviews.length ? (selReviews.reduce((s, r) => s + r.rating, 0) / selReviews.length).toFixed(1) : '—';

  /* Inline edits */
  const [editFields, setEditFields] = useState({});
  useEffect(() => {
    if (sel) setEditFields({
      status: sel.status || 'pending',
      commission_rate: sel.commission_rate ?? '',
      verified: !!sel.verified,
      tier: sel.tier || '',
    });
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const savePartner = async () => {
    if (!sel) return;
    setSaving(true);
    try {
      await supabase.from('partners').update({
        status: editFields.status,
        commission_rate: editFields.commission_rate === '' ? null : parseFloat(editFields.commission_rate),
        verified: editFields.verified,
        tier: editFields.tier || null,
        updated_at: new Date().toISOString(),
      }).eq('id', sel.id);
      await load();
    } catch (_e) {}
    setSaving(false);
  };

  const toggleListingActive = async (listingId, currentActive) => {
    await supabase.from('partner_listings').update({ active: !currentActive, updated_at: new Date().toISOString() }).eq('id', listingId);
    await load();
  };

  const addListing = async () => {
    if (!sel || !newListing.title || !newListing.category) return;
    setSaving(true);
    await supabase.from('partner_listings').insert({
      partner_id: sel.id,
      title: newListing.title,
      category: newListing.category,
      price_amount: newListing.price_amount ? parseFloat(newListing.price_amount) : null,
      price_currency: newListing.price_currency || 'USD',
      price_unit: newListing.price_unit || 'per session',
      description: newListing.description || null,
      active: true,
      sort_order: selListings.length,
    });
    setNewListing({ title: '', category: '', price_amount: '', price_currency: 'USD', price_unit: 'per session', description: '' });
    setAddingListing(false);
    await load();
    setSaving(false);
  };

  return (
    <>
      {/* KPI row */}
      <Grid cols={4}>
        <MetricCard label="Total partners" value={loading ? '…' : fmt(partners.length)} sub="All time" accent={C.teal} />
        <MetricCard label="Verified" value={loading ? '…' : fmt(verified)} sub={partners.length ? Math.round(verified / partners.length * 100) + '% of total' : undefined} accent={C.sage} trend={verified > 0 ? 'up' : null} />
        <MetricCard label="Pending applications" value={loading ? '…' : fmt(pending)} accent={C.amber} trend={pending > 0 ? 'down' : null} />
        <MetricCard label="Active listings" value={loading ? '…' : fmt(activeListings)} sub={listings.length ? fmt(listings.length) + ' total' : undefined} accent={C.mid} />
      </Grid>

      {/* Filters */}
      <Card>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, city, country..." style={{ maxWidth: 260, flex: '1 1 200px' }} />
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ maxWidth: 160, flex: '0 1 150px' }}>
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="pending_review">Pending review</option>
            <option value="suspended">Suspended</option>
          </Select>
          <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ maxWidth: 180, flex: '0 1 170px' }}>
            <option value="all">All types</option>
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
          <div style={{ fontSize: '.75rem', color: C.textMuted }}>{fmt(filtered.length)} partner{filtered.length !== 1 ? 's' : ''}</div>
        </div>
      </Card>

      {/* Partner list */}
      <Card style={{ padding: 0 }}>
        {loading ? Array.from({ length: 6 }).map((_, i) => <Skel key={i} h={44} mb={0} />) : filtered.length === 0 ? (
          <EmptyState title="No partners found" body="Try adjusting your filters." icon="🤝" />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem', fontFamily: F }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}`, background: '#fafaf8' }}>
                  {['Name', 'Type', 'Location', 'Verified', 'Listings', 'Status'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: '.72rem', fontWeight: 600, color: C.textMid, letterSpacing: '.02em', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const sp = statusPill(p.status);
                  return (
                    <tr key={p.id} onClick={() => setSelected(p.id)} style={{
                      borderBottom: `1px solid ${C.border}`, cursor: 'pointer',
                      background: selected === p.id ? '#f0f7fc' : 'transparent',
                      transition: 'background .15s',
                    }}
                      onMouseOver={e => { if (selected !== p.id) e.currentTarget.style.background = '#fafaf8'; }}
                      onMouseOut={e => { if (selected !== p.id) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <td style={{ padding: '11px 14px', fontWeight: 600, color: C.text }}>{p.name || '—'}</td>
                      <td style={{ padding: '11px 14px', color: C.textMid }}>{p.partner_type || '—'}</td>
                      <td style={{ padding: '11px 14px', color: C.textMid }}>{[p.city, p.country].filter(Boolean).join(', ') || '—'}</td>
                      <td style={{ padding: '11px 14px' }}>{p.verified ? <span style={{ color: C.green, fontWeight: 700 }}>Yes</span> : <span style={{ color: C.textMuted }}>No</span>}</td>
                      <td style={{ padding: '11px 14px', fontVariantNumeric: 'tabular-nums' }}>{listingsByPartner[p.id] || 0}</td>
                      <td style={{ padding: '11px 14px' }}><Pill type={sp.type}>{sp.label}</Pill></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Detail panel */}
      <SlidePanel open={!!sel} onClose={() => { setSelected(null); setAddingListing(false); }} width={420} isMobile={isMobile} title={sel?.name || 'Partner'}>
        {sel && (
          <>
            {/* Info */}
            <div style={{ fontSize: '.78rem', color: C.textMid, marginBottom: 16 }}>
              <div style={{ marginBottom: 3 }}><strong style={{ color: C.text }}>Email:</strong> {sel.email || '—'}</div>
              <div style={{ marginBottom: 3 }}><strong style={{ color: C.text }}>Phone:</strong> {sel.phone || '—'}</div>
              <div style={{ marginBottom: 3 }}><strong style={{ color: C.text }}>Address:</strong> {sel.address || '—'}</div>
              <div style={{ marginBottom: 3 }}><strong style={{ color: C.text }}>Type:</strong> {sel.partner_type || '—'}</div>
              <div style={{ marginBottom: 3 }}><strong style={{ color: C.text }}>Joined:</strong> {timeLabel(sel.created_at)}</div>
              {sel.stripe_account_id && <div style={{ marginBottom: 3 }}><strong style={{ color: C.text }}>Stripe:</strong> <span style={{ fontFamily: 'monospace', fontSize: '.75rem' }}>{sel.stripe_account_id}</span></div>}
            </div>

            {/* Editable fields */}
            <Card style={{ background: '#fafaf8', marginBottom: 14 }}>
              <CardTitle>Admin controls</CardTitle>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <FieldRow>
                  <Label>Status</Label>
                  <Select value={editFields.status || ''} onChange={e => setEditFields(f => ({ ...f, status: e.target.value }))}>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="pending_review">Pending review</option>
                    <option value="suspended">Suspended</option>
                  </Select>
                </FieldRow>
                <FieldRow>
                  <Label>Commission %</Label>
                  <Input value={editFields.commission_rate ?? ''} onChange={e => setEditFields(f => ({ ...f, commission_rate: e.target.value }))} placeholder="e.g. 15" />
                </FieldRow>
                <FieldRow>
                  <Label>Tier</Label>
                  <Select value={editFields.tier || ''} onChange={e => setEditFields(f => ({ ...f, tier: e.target.value }))}>
                    <option value="">None</option>
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                    <option value="passage">Passage</option>
                  </Select>
                </FieldRow>
                <FieldRow>
                  <Label>Verified</Label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '.82rem', color: C.text }}>
                    <input type="checkbox" checked={editFields.verified || false} onChange={e => setEditFields(f => ({ ...f, verified: e.target.checked }))} />
                    {editFields.verified ? 'Verified' : 'Not verified'}
                  </label>
                </FieldRow>
              </div>
              <div style={{ marginTop: 6 }}>
                <Btn onClick={savePartner} small>{saving ? 'Saving...' : 'Save changes'}</Btn>
              </div>
            </Card>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
              <StatTile value={selBookings.length} label="Bookings" color={C.teal} />
              <StatTile value={fmtCurrency(selRevenue, 'USD')} label="Revenue" color={C.sage} />
              <StatTile value={selAvgRating} label="Avg rating" color={C.brown} />
            </div>

            {/* Listings */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: '.78rem', color: C.textMid, fontWeight: 600, letterSpacing: '.01em' }}>LISTINGS ({selListings.length})</div>
                <Btn small variant="outline" onClick={() => setAddingListing(a => !a)}>{addingListing ? 'Cancel' : '+ Add listing'}</Btn>
              </div>

              {addingListing && (
                <Card style={{ background: '#f9faf8', marginBottom: 10 }}>
                  <FieldRow><Label>Title</Label><Input value={newListing.title} onChange={e => setNewListing(n => ({ ...n, title: e.target.value }))} placeholder="Listing title" /></FieldRow>
                  <FieldRow><Label>Category</Label><Input value={newListing.category} onChange={e => setNewListing(n => ({ ...n, category: e.target.value }))} placeholder="e.g. Spa, Gym, Clinic" /></FieldRow>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 1fr', gap: 8, marginBottom: 12 }}>
                    <div><Label>Price</Label><Input value={newListing.price_amount} onChange={e => setNewListing(n => ({ ...n, price_amount: e.target.value }))} placeholder="0.00" /></div>
                    <div><Label>Currency</Label><Select value={newListing.price_currency} onChange={e => setNewListing(n => ({ ...n, price_currency: e.target.value }))}><option>USD</option><option>EUR</option><option>GBP</option><option>THB</option><option>AED</option></Select></div>
                    <div><Label>Unit</Label><Input value={newListing.price_unit} onChange={e => setNewListing(n => ({ ...n, price_unit: e.target.value }))} placeholder="per session" /></div>
                  </div>
                  <FieldRow><Label>Description</Label><textarea value={newListing.description} onChange={e => setNewListing(n => ({ ...n, description: e.target.value }))} placeholder="Brief description..." style={{ fontSize: '.82rem', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 11px', color: C.text, fontFamily: F, width: '100%', minHeight: 60, resize: 'vertical', outline: 'none' }} /></FieldRow>
                  <Btn onClick={addListing} small variant="green">{saving ? 'Adding...' : 'Add listing'}</Btn>
                </Card>
              )}

              {selListings.length === 0 && !addingListing ? (
                <div style={{ fontSize: '.8rem', color: C.textMuted, padding: '12px 0' }}>No listings yet.</div>
              ) : selListings.map(l => (
                <div key={l.id} style={{
                  background: C.white, border: `1px solid ${C.border}`, borderRadius: 10,
                  padding: '12px 14px', marginBottom: 8,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: '.84rem', fontWeight: 600, color: C.text, marginBottom: 3 }}>{l.title}</div>
                      <div style={{ fontSize: '.75rem', color: C.textMid }}>
                        {l.category}{l.price_amount ? ` • ${fmtCurrency(l.price_amount, l.price_currency)} / ${l.price_unit || 'unit'}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {l.passage_only && <Pill type="amber">Passage</Pill>}
                      <button onClick={() => toggleListingActive(l.id, l.active)} style={{
                        background: l.active ? C.greenBg : C.redBg, border: 'none', borderRadius: 6,
                        padding: '3px 9px', fontSize: '.72rem', fontWeight: 600, cursor: 'pointer',
                        color: l.active ? C.greenText : C.redText, fontFamily: F,
                      }}>{l.active ? 'Active' : 'Inactive'}</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </SlidePanel>
    </>
  );
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   BOOKINGS TAB
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
function BookingsTab({ isMobile }) {
  const [bookings, setBookings] = useState([]);
  const [partners, setPartners] = useState([]);
  const [listings, setListings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [passageFilter, setPassageFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: b }, { data: p }, { data: l }] = await Promise.all([
        supabase.from('bookings').select('*').order('created_at', { ascending: false }).limit(500),
        supabase.from('partners').select('id,name'),
        supabase.from('partner_listings').select('id,title'),
      ]);
      setBookings(b || []);
      setPartners(p || []);
      setListings(l || []);
    } catch (_e) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  /* Load messages when a booking is selected */
  useEffect(() => {
    if (!selected) { setMessages([]); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('booking_messages').select('*').eq('booking_id', selected).order('created_at', { ascending: true });
      if (!cancelled) setMessages(data || []);
    })();
    return () => { cancelled = true; };
  }, [selected]);

  const partnerMap = {};
  partners.forEach(p => { partnerMap[p.id] = p.name; });
  const listingMap = {};
  listings.forEach(l => { listingMap[l.id] = l.title; });

  const filtered = bookings.filter(b => {
    if (search && !(b.booking_ref || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== 'all' && b.status !== statusFilter) return false;
    if (passageFilter === 'passage' && !b.is_passage) return false;
    if (passageFilter === 'standard' && b.is_passage) return false;
    return true;
  });

  const sel = selected ? bookings.find(b => b.id === selected) : null;

  const updateStatus = async (newStatus) => {
    if (!sel) return;
    setSaving(true);
    const updates = { status: newStatus, updated_at: new Date().toISOString() };
    if (newStatus === 'confirmed') updates.confirmed_at = new Date().toISOString();
    if (newStatus === 'completed') updates.completed_at = new Date().toISOString();
    if (newStatus === 'cancelled') updates.cancelled_at = new Date().toISOString();
    await supabase.from('bookings').update(updates).eq('id', sel.id);
    await load();
    setSaving(false);
  };

  return (
    <>
      {/* Filters */}
      <Card>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search booking ref..." style={{ maxWidth: 220, flex: '1 1 180px' }} />
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ maxWidth: 160, flex: '0 1 150px' }}>
            <option value="all">All statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </Select>
          <Select value={passageFilter} onChange={e => setPassageFilter(e.target.value)} style={{ maxWidth: 150, flex: '0 1 140px' }}>
            <option value="all">All tiers</option>
            <option value="passage">Passage only</option>
            <option value="standard">Standard only</option>
          </Select>
          <div style={{ fontSize: '.75rem', color: C.textMuted }}>{fmt(filtered.length)} booking{filtered.length !== 1 ? 's' : ''}</div>
        </div>
      </Card>

      {/* Bookings list */}
      <Card style={{ padding: 0 }}>
        {loading ? Array.from({ length: 6 }).map((_, i) => <Skel key={i} h={44} mb={0} />) : filtered.length === 0 ? (
          <EmptyState title="No bookings found" body="Try adjusting your filters." icon="📋" />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem', fontFamily: F }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}`, background: '#fafaf8' }}>
                  {['Ref', 'Partner', 'Listing', 'Status', 'Price', 'Date', 'Passage'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: '.72rem', fontWeight: 600, color: C.textMid, letterSpacing: '.02em', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(b => {
                  const sp = statusPill(b.status);
                  return (
                    <tr key={b.id} onClick={() => setSelected(b.id)} style={{
                      borderBottom: `1px solid ${C.border}`, cursor: 'pointer',
                      background: selected === b.id ? '#f0f7fc' : 'transparent',
                      transition: 'background .15s',
                    }}
                      onMouseOver={e => { if (selected !== b.id) e.currentTarget.style.background = '#fafaf8'; }}
                      onMouseOut={e => { if (selected !== b.id) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <td style={{ padding: '11px 14px', fontWeight: 600, color: C.text, fontFamily: 'monospace', fontSize: '.78rem' }}>{b.booking_ref || '#' + b.id}</td>
                      <td style={{ padding: '11px 14px', color: C.textMid }}>{partnerMap[b.partner_id] || '—'}</td>
                      <td style={{ padding: '11px 14px', color: C.textMid }}>{listingMap[b.listing_id] || '—'}</td>
                      <td style={{ padding: '11px 14px' }}><Pill type={sp.type}>{sp.label}</Pill></td>
                      <td style={{ padding: '11px 14px', fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(b.final_price || b.quoted_price, b.quoted_currency)}</td>
                      <td style={{ padding: '11px 14px', color: C.textMid }}>{timeLabel(b.created_at)}</td>
                      <td style={{ padding: '11px 14px' }}>{b.is_passage ? <Pill type="amber">Passage</Pill> : null}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Booking detail panel */}
      <SlidePanel open={!!sel} onClose={() => setSelected(null)} width={440} isMobile={isMobile} title={sel ? (sel.booking_ref || '#' + sel.id) : 'Booking'}>
        {sel && (
          <>
            {/* Booking info */}
            <div style={{ fontSize: '.78rem', color: C.textMid, marginBottom: 16, lineHeight: 1.8 }}>
              <div><strong style={{ color: C.text }}>Partner:</strong> {partnerMap[sel.partner_id] || '—'}</div>
              <div><strong style={{ color: C.text }}>Listing:</strong> {listingMap[sel.listing_id] || '—'}</div>
              <div><strong style={{ color: C.text }}>Type:</strong> {sel.booking_type || '—'}</div>
              <div><strong style={{ color: C.text }}>Status:</strong> <Pill type={statusPill(sel.status).type}>{statusPill(sel.status).label}</Pill></div>
              <div><strong style={{ color: C.text }}>Created:</strong> {timeLabel(sel.created_at)}</div>
              {sel.confirmed_at && <div><strong style={{ color: C.text }}>Confirmed:</strong> {timeLabel(sel.confirmed_at)}</div>}
              {sel.completed_at && <div><strong style={{ color: C.text }}>Completed:</strong> {timeLabel(sel.completed_at)}</div>}
              {sel.cancelled_at && <div><strong style={{ color: C.text }}>Cancelled:</strong> {timeLabel(sel.cancelled_at)}</div>}
            </div>

            {/* Price & payment */}
            <Card style={{ background: '#fafaf8', marginBottom: 14 }}>
              <CardTitle>Payment</CardTitle>
              <div style={{ fontSize: '.8rem', color: C.textMid, lineHeight: 1.8 }}>
                <div><strong style={{ color: C.text }}>Quoted:</strong> {fmtCurrency(sel.quoted_price, sel.quoted_currency)}</div>
                {sel.final_price && <div><strong style={{ color: C.text }}>Final:</strong> {fmtCurrency(sel.final_price, sel.quoted_currency)}</div>}
                {sel.commission_amount && <div><strong style={{ color: C.text }}>Commission:</strong> {fmtCurrency(sel.commission_amount, sel.quoted_currency)}</div>}
                <div><strong style={{ color: C.text }}>Payment status:</strong> <Pill type={sel.payment_status === 'paid' ? 'green' : sel.payment_status === 'refunded' ? 'red' : 'amber'}>{sel.payment_status || 'unpaid'}</Pill></div>
                {sel.stripe_payment_id && <div><strong style={{ color: C.text }}>Stripe:</strong> <span style={{ fontFamily: 'monospace', fontSize: '.75rem' }}>{sel.stripe_payment_id}</span></div>}
              </div>
            </Card>

            {/* Guests & special requests */}
            {(sel.guests || sel.special_requests) && (
              <Card style={{ background: '#fafaf8', marginBottom: 14 }}>
                <CardTitle>Guest details</CardTitle>
                {sel.guests && (
                  <div style={{ fontSize: '.8rem', color: C.textMid, marginBottom: 8 }}>
                    <pre style={{ margin: 0, fontFamily: F, whiteSpace: 'pre-wrap', fontSize: '.78rem' }}>
                      {typeof sel.guests === 'string' ? sel.guests : JSON.stringify(sel.guests, null, 2)}
                    </pre>
                  </div>
                )}
                {sel.special_requests && (
                  <div style={{ fontSize: '.8rem', color: C.text, background: '#fff8f0', borderRadius: 6, padding: '8px 10px', border: `1px solid ${C.amberBg}` }}>
                    {sel.special_requests}
                  </div>
                )}
              </Card>
            )}

            {/* Passage info */}
            {sel.is_passage && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <Pill type="amber">Passage {sel.passage_tier || ''}</Pill>
                {sel.concierge_handled && <Pill type="teal">Concierge handled</Pill>}
                {sel.sana_recommended && <Pill type="blue">Sana recommended</Pill>}
              </div>
            )}

            {/* Status actions */}
            <Card style={{ background: '#fafaf8', marginBottom: 14 }}>
              <CardTitle>Update status</CardTitle>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {sel.status !== 'confirmed' && <Btn small variant="primary" onClick={() => updateStatus('confirmed')}>{saving ? '...' : 'Confirm'}</Btn>}
                {sel.status !== 'completed' && <Btn small variant="green" onClick={() => updateStatus('completed')}>{saving ? '...' : 'Complete'}</Btn>}
                {sel.status !== 'cancelled' && <Btn small variant="danger" onClick={() => updateStatus('cancelled')}>{saving ? '...' : 'Cancel'}</Btn>}
              </div>
            </Card>

            {/* Messages thread */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: '.78rem', color: C.textMid, fontWeight: 600, letterSpacing: '.01em', marginBottom: 10 }}>MESSAGES ({messages.length})</div>
              {messages.length === 0 ? (
                <div style={{ fontSize: '.8rem', color: C.textMuted, padding: '8px 0' }}>No messages for this booking.</div>
              ) : messages.map(m => (
                <MessageBubble key={m.id} msg={m} />
              ))}
            </div>
          </>
        )}
      </SlidePanel>
    </>
  );
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   MESSAGES TAB
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
function MessageBubble({ msg }) {
  const colors = {
    user: { bg: C.blueBg, border: '#dce8ff', label: 'User', color: C.blueText },
    partner: { bg: C.greenBg, border: '#c0e8d4', label: 'Partner', color: C.greenText },
    admin: { bg: '#e4eef2', border: '#c8dce6', label: 'Admin', color: C.teal },
  };
  const c = colors[msg.sender_type] || colors.admin;
  return (
    <div style={{
      background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10,
      padding: '10px 13px', marginBottom: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: '.72rem', fontWeight: 700, color: c.color, textTransform: 'uppercase', letterSpacing: '.02em' }}>{c.label}</span>
        <span style={{ fontSize: '.7rem', color: C.textMuted }}>{timeAgo(msg.created_at)}{msg.read_at ? '' : ' • unread'}</span>
      </div>
      <div style={{ fontSize: '.82rem', color: C.text, lineHeight: 1.55 }}>{msg.message}</div>
      {msg.attachments && Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
        <div style={{ marginTop: 6, fontSize: '.72rem', color: C.textMid }}>{msg.attachments.length} attachment{msg.attachments.length > 1 ? 's' : ''}</div>
      )}
    </div>
  );
}

function MessagesTab({ isMobile, session }) {
  const [groups, setGroups] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [thread, setThread] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const threadEndRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: msgs }, { data: bks }, { data: pts }] = await Promise.all([
        supabase.from('booking_messages').select('*').order('created_at', { ascending: false }).limit(1000),
        supabase.from('bookings').select('id,booking_ref,partner_id'),
        supabase.from('partners').select('id,name'),
      ]);
      setBookings(bks || []);
      setPartners(pts || []);

      /* Group messages by booking_id */
      const gMap = {};
      (msgs || []).forEach(m => {
        if (!gMap[m.booking_id]) gMap[m.booking_id] = { booking_id: m.booking_id, messages: [], unread: 0, latest: m.created_at };
        gMap[m.booking_id].messages.push(m);
        if (!m.read_at) gMap[m.booking_id].unread++;
        if (m.created_at > gMap[m.booking_id].latest) gMap[m.booking_id].latest = m.created_at;
      });
      const sorted = Object.values(gMap).sort((a, b) => (b.latest || '').localeCompare(a.latest || ''));
      setGroups(sorted);
    } catch (_e) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  /* Load thread when booking selected */
  useEffect(() => {
    if (!selectedBooking) { setThread([]); return; }
    const g = groups.find(g => g.booking_id === selectedBooking);
    if (g) {
      const sorted = [...g.messages].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
      setThread(sorted);
    }
  }, [selectedBooking, groups]);

  useEffect(() => {
    if (threadEndRef.current) threadEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [thread]);

  const partnerMap = {};
  partners.forEach(p => { partnerMap[p.id] = p.name; });
  const bookingMap = {};
  bookings.forEach(b => { bookingMap[b.id] = b; });

  const sendMessage = async () => {
    if (!newMsg.trim() || !selectedBooking) return;
    setSending(true);
    await supabase.from('booking_messages').insert({
      booking_id: selectedBooking,
      sender_type: 'admin',
      sender_id: session?.user?.id || null,
      message: newMsg.trim(),
    });
    setNewMsg('');
    await load();
    setSending(false);
  };

  const selBookingInfo = selectedBooking ? bookingMap[selectedBooking] : null;

  return (
    <div style={{ display: 'flex', gap: 14, minHeight: 500, flexDirection: isMobile ? 'column' : 'row' }}>
      {/* Conversation list */}
      <Card style={{ flex: isMobile ? 'none' : '0 0 340px', padding: 0, overflowY: 'auto', maxHeight: isMobile ? 300 : 700 }}>
        <div style={{ padding: '14px 16px 8px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: '.78rem', fontWeight: 600, color: C.textMid, letterSpacing: '.01em' }}>Conversations ({groups.length})</div>
        </div>
        {loading ? Array.from({ length: 5 }).map((_, i) => <Skel key={i} h={52} mb={0} />) : groups.length === 0 ? (
          <EmptyState title="No messages yet" body="Messages will appear here when partners and users communicate." icon="💬" />
        ) : groups.map(g => {
          const bk = bookingMap[g.booking_id];
          const partnerName = bk ? (partnerMap[bk.partner_id] || 'Unknown') : 'Unknown';
          const ref = bk?.booking_ref || '#' + g.booking_id;
          const lastMsg = g.messages[0]; // already sorted desc
          return (
            <div key={g.booking_id} onClick={() => setSelectedBooking(g.booking_id)} style={{
              padding: '12px 16px', borderBottom: `1px solid ${C.border}`, cursor: 'pointer',
              background: selectedBooking === g.booking_id ? '#f0f7fc' : 'transparent',
              transition: 'background .15s',
            }}
              onMouseOver={e => { if (selectedBooking !== g.booking_id) e.currentTarget.style.background = '#fafaf8'; }}
              onMouseOut={e => { if (selectedBooking !== g.booking_id) e.currentTarget.style.background = selectedBooking === g.booking_id ? '#f0f7fc' : 'transparent'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                <span style={{ fontSize: '.82rem', fontWeight: 600, color: C.text }}>{ref}</span>
                <span style={{ fontSize: '.7rem', color: C.textMuted }}>{timeAgo(g.latest)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '.75rem', color: C.textMid }}>{partnerName}</span>
                {g.unread > 0 && (
                  <span style={{
                    background: C.teal, color: '#fff', borderRadius: 10, padding: '1px 7px',
                    fontSize: '.68rem', fontWeight: 700, minWidth: 18, textAlign: 'center',
                  }}>{g.unread}</span>
                )}
              </div>
              {lastMsg && (
                <div style={{ fontSize: '.74rem', color: C.textMuted, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>
                  {lastMsg.message}
                </div>
              )}
            </div>
          );
        })}
      </Card>

      {/* Thread view */}
      <Card style={{ flex: 1, padding: 0, display: 'flex', flexDirection: 'column', maxHeight: isMobile ? 500 : 700 }}>
        {!selectedBooking ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, padding: 40 }}>
            <div style={{ textAlign: 'center', color: C.textMuted, fontSize: '.85rem' }}>Select a conversation to view messages</div>
          </div>
        ) : (
          <>
            {/* Thread header */}
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, background: '#fafaf8' }}>
              <div style={{ fontSize: '.85rem', fontWeight: 700, color: C.text }}>
                {selBookingInfo?.booking_ref || '#' + selectedBooking}
              </div>
              <div style={{ fontSize: '.74rem', color: C.textMid }}>
                {selBookingInfo ? (partnerMap[selBookingInfo.partner_id] || 'Unknown partner') : ''}
                {' • '}{thread.length} message{thread.length !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
              {thread.map(m => <MessageBubble key={m.id} msg={m} />)}
              <div ref={threadEndRef} />
            </div>

            {/* Compose */}
            <div style={{ borderTop: `1px solid ${C.border}`, padding: '12px 16px', display: 'flex', gap: 8, background: '#fafaf8' }}>
              <input
                value={newMsg}
                onChange={e => setNewMsg(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Type an admin message..."
                style={{
                  flex: 1, fontSize: '.84rem', border: `1px solid ${C.border}`, borderRadius: 8,
                  padding: '9px 12px', color: C.text, outline: 'none', fontFamily: F, background: C.white,
                }}
              />
              <Btn onClick={sendMessage} small>{sending ? '...' : 'Send'}</Btn>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   MAIN EXPORT
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
const TABS = [
  { key: 'partners', label: 'Partners' },
  { key: 'bookings', label: 'Bookings' },
  { key: 'messages', label: 'Messages' },
];

export default function Partners({ session }) {
  const [tab, setTab] = useState('partners');
  const isMobile = useIsMobile();

  return (
    <div style={{ fontFamily: F }}>
      <style>{'@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}'}</style>

      {/* Page header */}
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: '1.3rem', fontWeight: 700, color: C.text, margin: 0, letterSpacing: '-.01em' }}>Partner Portal</h1>
        <p style={{ fontSize: '.8rem', color: C.textMid, margin: '4px 0 0' }}>Manage partners, bookings and communications</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 18, borderBottom: `2px solid ${C.border}` }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            background: 'none', border: 'none', borderBottom: tab === t.key ? `2px solid ${C.teal}` : '2px solid transparent',
            padding: '10px 20px', fontSize: '.82rem', fontWeight: tab === t.key ? 700 : 500,
            color: tab === t.key ? C.teal : C.textMid, cursor: 'pointer', fontFamily: F,
            marginBottom: -2, transition: 'color .15s, border-color .15s',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'partners' && <PartnersTab isMobile={isMobile} />}
      {tab === 'bookings' && <BookingsTab isMobile={isMobile} />}
      {tab === 'messages' && <MessagesTab isMobile={isMobile} session={session} />}
    </div>
  );
}
